import React, { useContext, useEffect, useState } from "react";
import { Modal, Box, Typography, CircularProgress } from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import NeoButton from "./NeoButton";
import { useAuthStore } from "../@shared/store/useAuthStore";
import badge1 from "../@shared/assets/badges/1st.png";
import badge2 from "../@shared/assets/badges/2nd.png";
import badge3 from "../@shared/assets/badges/3rd.png";
import leaderboardBg from "../@shared/assets/leader-board-bg.png";
import { Close } from "@mui/icons-material";
import { MainContext } from "../App";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../@shared/firebase/config";

const screenHeight = window.innerHeight;
const modalHeight = screenHeight * 0.8;
const modalWidth = screenHeight * (4 / 9);

const overlayStyle = {
  position: "fixed",
  top: 0,
  left: 0,
  width: "100vw",
  height: "100vh",
  backgroundColor: "rgba(0,0,0,0.6)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  zIndex: 1400,
};
const modalWrapperStyle = {
  width: modalWidth,
  height: modalHeight,
};
const modalContentStyle = {
  width: modalWidth,
  height: modalHeight,
  backgroundImage: `url(${leaderboardBg})`,
  backgroundRepeat: "no-repeat",
  backgroundSize: "contain",
  position: "relative",
  padding: "10%",
  display: "flex",
  top: window.innerWidth >= 900 ? "-5%" : "-10%",
  left: window.innerWidth >= 900 ? "0%" : "-5%",
  flexDirection: "column",
};
const codeRowStyle = {
  display: "flex",
  justifyContent: "space-between",
  marginBottom: 8,
  marginTop: "6%",
};
const scrollAreaStyle = {
  flex: 1,
  overflowY: "auto",
  marginTop: 8,
  scrollbarWidth: "none",
  width: "100%",
  marginBottom: "10px",
};
const roomRowBase = {
  backgroundColor: "#4e2a9b",
  padding: "12px",
  borderRadius: 2,
  display: "flex",
  alignItems: "center",
  gap: "2%",
  marginBottom: "3%",
};

export default function LeaderBoardModal({ visible, onClose }) {
  const { userData } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [players, setPlayers] = useState([]);
  const [selectedType, setSelectedType] = useState("XP");
  const { Theme } = useContext(MainContext);

  useEffect(() => {
    if (!visible) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        const docRef = doc(db, "leaderboards", selectedType.toLowerCase());
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const data = docSnap.data();
          if (selectedType === "Wins") {
            setPlayers(
              data.players.sort((a, b) => b.record.wins - a.record.wins) || []
            );
          } else {
            setPlayers(data.players || []);
          }
        } else {
          setPlayers([]);
        }
      } catch (error) {
        console.error("Error loading leaderboard:", error);
        setPlayers([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [visible, selectedType]);

  const handleTypeChange = (type) => setSelectedType(type);

  const animationVariants = {
    hidden: { y: -50, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { duration: 0.3 } },
    exit: { y: -50, opacity: 0, transition: { duration: 0.2 } },
  };

  return (
    <Modal open={visible} onClose={onClose} closeAfterTransition>
      <AnimatePresence>
        {visible && (
          <Box
            component={motion.div}
            animate="visible"
            exit="exit"
            variants={animationVariants}
            style={overlayStyle}
          >
            <Box
              component={motion.div}
              animate="visible"
              exit="exit"
              variants={animationVariants}
              style={modalWrapperStyle}
            >
              <Box style={modalContentStyle}>
                <Box
                  style={{
                    width: "90%",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    alignItems: "center",
                    position: "relative",
                    overflowY: "hidden",
                  }}
                >
                  <Typography
                    variant="h5"
                    align="center"
                    sx={{
                      mb: 1,
                      color: "purple",
                      marginTop: "10%",
                      fontWeight: "bold",

                    }}
                  >
                    LEADERBOARDS
                  </Typography>

                  <Box style={codeRowStyle}>
                    {["XP", "Wins", "Ranked"].map((type) => (
                      <NeoButton
                        key={type}
                        width={type === "Ranked" ? 36 : 30}
                        text={type}
                        color={
                          selectedType === type
                            ? Theme.palette.primary.main
                            : Theme.palette.secondary.main
                        }
                        onClick={() => handleTypeChange(type)}
                      />
                    ))}
                  </Box>

                  <Typography align="center" sx={{ color: "#fff", mb: 1 }}>
                    {selectedType} Leaderboard
                  </Typography>

                  {loading ? (
                    <Box
                      sx={{
                        flex: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexDirection: "column",
                      }}
                    >
                      <CircularProgress color="inherit" />
                      <Typography sx={{ color: "#fff", mt: 1 }}>
                        Loading leaderboards...
                      </Typography>
                    </Box>
                  ) : (
                    <Box style={scrollAreaStyle}>
                      {players.length === 0 ? (
                        <Typography align="center" sx={{ color: "#fff" }}>
                          No players available
                        </Typography>
                      ) : (
                        players.map((p, idx) => {
                          const isTop = idx < 3;
                          const isSelf = userData?.id === p.id;

                          const topStyles = [
                            {
                              bg: "#ffd700",
                              shadow: "0 2px 4px rgba(255,215,0,0.8)",
                            },
                            {
                              bg: "#c0c0c0",
                              shadow: "0 2px 4px rgba(192,192,192,0.8)",
                            },
                            {
                              bg: "#cd7f32",
                              shadow: "0 2px 4px rgba(205,127,50,0.8)",
                            },
                          ];
                          const rowStyle = isTop
                            ? {
                              ...roomRowBase,
                              backgroundColor: topStyles[idx].bg,
                              boxShadow: topStyles[idx].shadow,
                            }
                            : roomRowBase;

                          return (
                            <Box key={p.id || p.username} sx={rowStyle}>
                              <Box sx={{ width: 40, textAlign: "center" }}>
                                {idx === 0 ? (
                                  <img
                                    src={badge1}
                                    alt="1st"
                                    width={40}
                                    height={50}
                                  />
                                ) : idx === 1 ? (
                                  <img
                                    src={badge2}
                                    alt="2nd"
                                    width={40}
                                    height={50}
                                  />
                                ) : idx === 2 ? (
                                  <img
                                    src={badge3}
                                    alt="3rd"
                                    width={40}
                                    height={50}
                                  />
                                ) : (
                                  <Typography
                                    sx={{
                                      color: "#fff",
                                      fontWeight: "bold",
                                      fontSize: 20,
                                    }}
                                  >
                                    {idx + 1}
                                  </Typography>
                                )}
                              </Box>
                              <Box sx={{ flex: 1 }}>
                                <Typography
                                  sx={{
                                    fontWeight: "bold",
                                    fontSize: isTop ? 18 : 16,
                                    color: isSelf
                                      ? "#ffd700"
                                      : isTop
                                        ? "#000"
                                        : "#fff",
                                    textShadow: isTop
                                      ? ` -1px -1px 0 #fff, 1px -1px 0 #fff, -1px  1px 0 #fff, 1px  1px 0 #fff`
                                      : "",
                                  }}
                                >
                                  {p.username} {isSelf && "(You)"}
                                </Typography>
                                <Typography
                                  sx={{
                                    fontSize: 14,
                                    color: isTop ? "#222" : "#ccc",
                                  }}
                                >
                                  {selectedType === "Wins"
                                    ? p.record.wins
                                    : p.value}{" "}
                                  {selectedType === "XP"
                                    ? "XP"
                                    : selectedType === "Wins"
                                      ? "Wins"
                                      : "Level"}
                                </Typography>
                              </Box>
                            </Box>
                          );
                        })
                      )}
                    </Box>
                  )}

                  <Box
                    sx={{
                      position: "absolute",
                      bottom: "0%",
                      left: "50%",
                      transform: "translateX(-50%)",
                      width: "80%",
                      bgcolor: Theme.palette.primary.main,
                      p: 1,
                      borderRadius: 1,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ color: "#fff", fontWeight: "bold" }}>
                        @{userData.username}
                      </Typography>
                      <Typography sx={{ color: "#ccc" }}>
                        {userData.xp} XP | {userData.record.wins} Wins | Level{" "}
                        {userData.level}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                <Box
                  sx={{
                    position: "absolute",
                    bottom: "-2%",
                    left: "45%",
                    transform: "translateX(-50%)",
                  }}
                >
                  <NeoButton
                    isIcon={true}
                    icon={<Close size={30} />}
                    color={Theme.palette.secondary.main}
                    onClick={onClose}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        )}
      </AnimatePresence>
    </Modal>
  );
}
